import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check } from "lucide-react";
import GoogleOAuthButton from "@/components/GoogleOAuthButton";
import NameFields from "@/components/NameFields";
import {
    ACCOUNT_TYPES,
    ACCOUNT_TYPE_LABEL,
    ACCOUNT_TYPE_TAGLINE,
    ACCOUNT_TYPE_FEATURES,
    ACCOUNT_TYPE_PRICE,
    ACCOUNT_TYPE_TO_PROFILES,
    PRO_TITLES,
    BETA_NOTICE,
} from "@/lib/profiles";

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [form, setForm] = useState({
        first_name: "",
        middle_initial: "",
        last_name: "",
        email: "",
        username: "",
        password: "",
    });
    const [accountType, setAccountType] = useState("standard");
    const [proTitle, setProTitle] = useState(""); // optional, Pro only
    const [ageConfirmed, setAgeConfirmed] = useState(false); // COPPA: must be 13+
    const [submitting, setSubmitting] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!ageConfirmed) {
            toast.error("Please confirm you are at least 13 years old to create an account");
            return;
        }
        if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.username.trim() || !form.password) {
            toast.error("First name, last name, email, username and password are required");
            return;
        }
        if (!/^[a-zA-Z0-9._-]+$/.test(form.username)) {
            toast.error("Username can only contain letters, numbers, and . _ -");
            return;
        }
        if (siteKey && !captchaToken) {
            toast.error("Please complete the human-verification check below");
            return;
        }
        setSubmitting(true);
        const profiles = ACCOUNT_TYPE_TO_PROFILES[accountType] || [];
        const pro_title = accountType === "pro" && proTitle ? proTitle : null;
        const mi = form.middle_initial ? `${form.middle_initial}. ` : "";
        const fullName = `${form.first_name.trim()} ${mi}${form.last_name.trim()}`.trim();
        const res = await register(fullName, form.email, form.password, form.username, profiles, pro_title, captchaToken);
        setSubmitting(false);
        if (res.ok) {
            toast.success("Welcome — check your email to verify your address");
            navigate("/account");
        } else {
            toast.error(res.error);
            setCaptchaToken(""); // force a fresh challenge on retry (tokens are single-use)
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-6 py-12" data-testid="register-page">
            <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-2">Create your account</div>
            <h1 className="font-display text-5xl tracking-wide">Join the portal</h1>
            <p className="text-sm text-[#a0a0a0] mt-2">
                Pick the account that fits you. You can upgrade or adjust things later from your Account page.
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
                {/* Account type */}
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-3">Choose your account type</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start" data-testid="account-type-picker">
                        {ACCOUNT_TYPES.map((t) => {
                            const active = accountType === t;
                            const price = ACCOUNT_TYPE_PRICE[t];
                            return (
                                <button
                                    key={t}
                                    type="button"
                                    data-testid={`account-type-${t}`}
                                    aria-pressed={active}
                                    onClick={() => setAccountType(t)}
                                    className={`text-left p-4 border-2 transition-colors ${
                                        active ? "border-[#ff4500] bg-[#ff4500]/10" : "border-[#2a2a2a] bg-[#141414] hover:border-[#3a3a3a]"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-display text-2xl tracking-wide">{ACCOUNT_TYPE_LABEL[t]}</div>
                                        <div className={`h-5 w-5 border ${active ? "bg-[#ff4500] border-[#ff4500]" : "border-[#3a3a3a]"} flex items-center justify-center`}>
                                            {active && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                    </div>
                                    <div className="text-xs text-[#a0a0a0] mt-1">{ACCOUNT_TYPE_TAGLINE[t]}</div>
                                    <div className={`mt-3 text-[11px] uppercase tracking-[0.2em] ${price.free ? "text-[#22c55e]" : "text-[#ff4500]"}`}>
                                        {price.free ? "Free" : `$${price.annual.toFixed(2)}/yr`}
                                    </div>
                                    {!price.free && (
                                        <div data-testid={`beta-notice-${t}`} className="mt-2 text-[11px] leading-snug text-[#22c55e] normal-case tracking-normal">
                                            {BETA_NOTICE}
                                        </div>
                                    )}
                                    <ul className="mt-3 space-y-1.5">
                                        {ACCOUNT_TYPE_FEATURES[t].map((f, i) => {
                                            const soon = f.endsWith("*");
                                            const text = soon ? f.slice(0, -1) : f;
                                            const isIntro = f.endsWith(":");
                                            return (
                                                <li key={i} className={`text-[11px] leading-snug ${isIntro ? "text-[#cfcfcf] font-semibold" : "text-[#a0a0a0]"} flex gap-1.5`}>
                                                    {!isIntro && <Check className="h-3 w-3 text-[#ff4500] shrink-0 mt-0.5" />}
                                                    <span>{text}{soon && <span className="text-[#ff4500]"> *</span>}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </button>
                            );
                        })}
                    </div>
                    {ACCOUNT_TYPE_FEATURES[accountType].some((f) => f.endsWith("*")) && (
                        <p className="text-[10px] text-[#a0a0a0] mt-2" data-testid="coming-soon-note">* Coming soon.</p>
                    )}
                </div>

                {/* COPPA: age gate — account creation is disabled until confirmed */}
                <div>
                    <button
                        type="button"
                        onClick={() => setAgeConfirmed((v) => !v)}
                        aria-pressed={ageConfirmed}
                        data-testid="age-confirm-checkbox"
                        className={`inline-flex items-center gap-3 px-4 py-3 border-2 text-left transition-colors ${
                            ageConfirmed ? "border-[#22c55e] bg-[#22c55e]/10" : "border-[#2a2a2a] bg-[#141414] hover:border-[#3a3a3a]"
                        }`}
                    >
                        <span className={`h-5 w-5 shrink-0 border flex items-center justify-center ${ageConfirmed ? "bg-[#22c55e] border-[#22c55e]" : "border-[#3a3a3a]"}`}>
                            {ageConfirmed && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                        </span>
                        <span className="text-sm text-white">I am at least 13 years old. <span className="text-[#a0a0a0]">(Minimum age to create an account)</span></span>
                    </button>
                    <p className="text-[11px] text-[#a0a0a0] mt-1.5" data-testid="age-tos-note">
                        Lying about your age is a violation of our Terms of Service.
                    </p>
                </div>

                {/* Pro title (optional) */}
                {accountType === "pro" && (
                    <div data-testid="pro-title-block">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0] mb-2">Your title (optional)</div>
                        <div className="flex flex-wrap gap-2">
                            {PRO_TITLES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    data-testid={`pro-title-${t.toLowerCase()}`}
                                    aria-pressed={proTitle === t}
                                    onClick={() => setProTitle((cur) => (cur === t ? "" : t))}
                                    className={`px-4 h-10 border text-xs uppercase tracking-[0.15em] transition-colors ${
                                        proTitle === t ? "border-[#ff4500] bg-[#ff4500]/10 text-[#ff4500]" : "border-[#2a2a2a] bg-[#141414] text-[#cfcfcf] hover:border-[#3a3a3a]"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                        <p className="text-[10px] text-[#a0a0a0] mt-1.5">Shows on your profile. You can change or clear it anytime in settings.</p>
                    </div>
                )}

                <GoogleOAuthButton label="Sign up with Google" disabled={!ageConfirmed} />
                <div className="flex items-center gap-3 py-1">
                    <div className="h-px flex-1 bg-[#2a2a2a]" />
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#666]">or with email</span>
                    <div className="h-px flex-1 bg-[#2a2a2a]" />
                </div>
                <NameFields
                    testidPrefix="reg"
                    section="register"
                    first={form.first_name}
                    mi={form.middle_initial}
                    last={form.last_name}
                    onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <F label="Email" type="email" testid="reg-email" value={form.email} onChange={on("email")} required autoComplete="email" />
                        <p className="text-[10px] text-[#ff4500] mt-1">Your email can't be changed later, so use one you'll keep.</p>
                    </div>
                    <F label="Username (used in your header and future page URL)" testid="reg-username" value={form.username} onChange={on("username")} required minLength={3} maxLength={40} pattern="[a-zA-Z0-9._-]+" placeholder="e.g. coach.johnson" autoComplete="username" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <F label="Password (min 6 chars)" type="password" testid="reg-password" value={form.password} onChange={on("password")} required minLength={6} autoComplete="new-password" />
                </div>

                {siteKey && (
                    <div data-testid="turnstile-widget">
                        <Turnstile
                            siteKey={siteKey}
                            options={{ theme: "dark", action: "register" }}
                            onSuccess={(t) => setCaptchaToken(t)}
                            onError={() => setCaptchaToken("")}
                            onExpire={() => setCaptchaToken("")}
                        />
                    </div>
                )}

                <Button
                    type="submit"
                    disabled={submitting || !ageConfirmed || (siteKey && !captchaToken)}
                    data-testid="register-submit"
                    className="w-full h-12 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {submitting ? "Creating account…" : "Create account"}
                </Button>
                {!ageConfirmed && (
                    <p className="text-[11px] text-[#a0a0a0] text-center" data-testid="age-gate-hint">
                        Check the box above to confirm you're at least 13 years old.
                    </p>
                )}
            </form>

            <p className="text-sm text-[#a0a0a0] mt-6">
                Already have an account?{" "}
                <Link to="/login" className="text-[#ff4500] hover:underline" data-testid="link-to-login">Log in</Link>
            </p>
        </div>
    );
}

function F({ label, testid, ...props }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</Label>
            <Input data-testid={testid} {...props} className="mt-2 h-11 rounded-none bg-[#141414] border-[#2a2a2a]" />
        </div>
    );
}
